import { alpha, type SxProps, type Theme } from "@mui/material/styles";

/** One palette colour per role, used for avatars, badges and role-card icons alike. */
export type RoleTone = "primary" | "info" | "warning" | "success" | "secondary";

export const stack: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
});

export const section: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
});

export const sectionHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 1,
};

export const sectionTitle: SxProps<Theme> = {
  fontSize: "0.84375rem",
  fontWeight: 800,
};

export const sectionHint: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const row: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  p: theme.spacing(1.25, 1.5),
  borderRadius: 1.5,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  flexWrap: { xs: "wrap", sm: "nowrap" },
});

export const avatar =
  (tone: RoleTone): SxProps<Theme> =>
  (theme) => ({
    width: 38,
    height: 38,
    borderRadius: 1.25,
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: alpha(theme.palette[tone].main, 0.12),
  });

export const rowText: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
};

export const rowTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const rowMeta: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const roleChip =
  (tone: RoleTone): SxProps<Theme> =>
  (theme) => ({
    display: "inline-flex",
    alignItems: "center",
    px: 1,
    py: 0.25,
    borderRadius: 999,
    fontSize: "0.6875rem",
    fontWeight: 700,
    color: theme.palette[tone].main,
    bgcolor: alpha(theme.palette[tone].main, 0.1),
    whiteSpace: "nowrap",
  });

export const rowActions: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flex: "none",
  marginInlineStart: { xs: "auto", sm: 0 },
};

export const roleSelect: SxProps<Theme> = (theme) => ({
  minWidth: 150,
  "& .MuiSelect-select": { py: 0.875, fontSize: "0.75rem", fontWeight: 600 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider },
});

export const expiredText: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "error.main",
  fontWeight: 600,
};

export const emptyBox: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  p: 1.5,
  borderRadius: 1.5,
  border: `1px dashed ${theme.palette.divider}`,
  color: "text.secondary",
  fontSize: "0.75rem",
});

export const roleGrid: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: theme.spacing(1.25),
});

export const roleCard =
  (selected: boolean, disabled: boolean, tone: RoleTone): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
    p: 1.5,
    borderRadius: 1.5,
    border: `1.5px solid ${selected ? theme.palette[tone].main : theme.palette.divider}`,
    bgcolor: selected ? alpha(theme.palette[tone].main, 0.05) : "background.paper",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    textAlign: "start",
    fontFamily: "inherit",
    color: "inherit",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": disabled ? {} : { borderColor: theme.palette[tone].main },
  });

export const roleCardTitle: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
};

export const roleCardDescription: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
  lineHeight: 1.7,
};

export const inviteRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.25),
  flexDirection: { xs: "column", sm: "row" },
});

export const inviteButton: SxProps<Theme> = {
  flex: "none",
  minWidth: 150,
  height: 56,
  width: { xs: "100%", sm: "auto" },
};

export const notice =
  (tone: "success" | "error"): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    p: 1.5,
    borderRadius: 1.5,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette[tone].main,
    bgcolor: alpha(theme.palette[tone].main, 0.06),
    border: `1px solid ${alpha(theme.palette[tone].main, 0.2)}`,
  });
